import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { concat, Observable, of } from 'rxjs';
import {
  ActionsEnum,
  FetchTask,
  fetchTask,
  FetchTaskError,
  fetchTaskError,
  IHandleInsightsHubDeeplink,
  LoadPromoBanners,
  PopCurrentPromoBanner,
  setPromoBanners,
  SetPromoBanners,
  setTask,
  SetTask,
  setTaskCompletionError,
  SetTaskCompletionError,
  SetTaskItemState,
  SetTaskItemStateError,
  setTaskItemStateError,
  SetTaskItemStateSuccess,
  setTaskItemStateSuccess,
  TriggerPostPurchaseSurvey,
} from './home-data.actions';
import {
  catchError,
  concatMap,
  filter,
  flatMap,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AccountDataStore } from '../account-data/account-data.store';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Account } from '../account-data/account.model';
import { HomeDataBuilder } from './home-data.builder';
import { HomeDataStore } from './home-data.store';
import { PromoBanner } from './models';
import { List } from 'immutable';
import {
  SetLocalStorageItem,
  setLocalStorageItem,
} from '../browser-manager-data/browser-manager-data.actions';
import { Settings } from '../settings-data/settings.model';
import { Features } from '../features-data/sub-models/features.model';
import {
  saveCurrentSettingsNetwork,
  SaveSettingsNetwork,
  updateSettings,
} from '../settings-data/settings-data.actions';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { MAX_POST_PURCHASE_SURVEY_COUNTER } from '../settings-data/settings-data.data';
import { PostPurchaseStateEnum } from '../settings-data/sub-models/post-purchase-info.model';
import { v4 } from 'uuid';
import { DialogService } from '../dialog/dialog.service';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { POST_PURCHASE_DIALOG_TYPE } from './home-data.data';
import { HomeDataService } from './home-data.service';
import { ActionsEnum as AuthActionsEnum } from '../auth-data/auth-data.actions';
import { trackEvent, TrackEvent } from '../tracking/tracking.actions';
import { IDeepLinkProperties } from '../navigation/deep-link-properties.interface';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { NavigationBuilder } from '../navigation/navigation.builder';
import { NavigationService } from '../navigation/navigation.service';
import { TrackingObjectBuilder } from '../tracking/tracking-object.builder';
import { TrackingAdditionalPropertiesBuilder } from '../tracking/tracking-additional-properties.builder';

@Injectable()
export class HomeDataEffects {
  constructor(
    private _actions: Actions,
    private _homeDataStore: HomeDataStore,
    private _accountDataStore: AccountDataStore,
    private _featuresDataStore: FeaturesDataStore,
    private _settingsDataStore: SettingsDataStore,
    private _homeDataBuilder: HomeDataBuilder,
    private _dialogService: DialogService,
    private _localStorageService: LocalStorageService,
    private _homeDataService: HomeDataService,
    private _navigationBuilder: NavigationBuilder,
    private _navigationService: NavigationService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingAdditionalPropertiesBuilder: TrackingAdditionalPropertiesBuilder
  ) {}

  @Effect()
  onAuthentication: Observable<FetchTask> = this._actions.pipe(
    ofType(AuthActionsEnum.AUTHENTICATED),
    take(1),
    switchMap(() => {
      return this._featuresDataStore.featureToggles$.pipe(
        filter(
          (toggles: Toggles) =>
            toggles.isAllAccess(Toggles.KEYS.FTU_EXPERIMENT) &&
            toggles.isAllAccess(Toggles.KEYS.FTU_TASK_LIST)
        ),
        map(() => fetchTask())
      );
    })
  );

  @Effect()
  loadPromoBanners: Observable<SetPromoBanners> = this._actions.pipe(
    ofType(ActionsEnum.LOAD_PROMO_BANNERS),
    switchMap(() => {
      return this._accountDataStore.getAccount().pipe(
        withLatestFrom(
          this._featuresDataStore.features$,
          this._settingsDataStore.settings$
        ),
        map(([account, features, settings]: [Account, Features, Settings]) => {
          const promoBanners = this._homeDataBuilder.buildPromoBannerStack(
            account.accountId,
            features,
            settings
          );

          return setPromoBanners(promoBanners);
        })
      );
    })
  );

  @Effect()
  popCurrentPromoBanner: Observable<
    SetLocalStorageItem | PopCurrentPromoBanner
  > = this._actions.pipe(
    ofType(ActionsEnum.POP_CURRENT_PROMO_BANNER),
    concatMap(() => {
      return this._homeDataStore.getPromoBanners().pipe(
        filter(
          (promoBanners: List<PromoBanner>) =>
            promoBanners && promoBanners.size > 0
        ),
        take(1),
        flatMap((promoBanners: List<PromoBanner>) => {
          const actions = [];
          const currentPromoBanner = promoBanners.first();
          const localStorageKey = currentPromoBanner.localStorageKey;

          if (localStorageKey) {
            actions.push(setLocalStorageItem(localStorageKey, true)); // mark as dismissed
          }

          actions.push(setPromoBanners(promoBanners.shift()));

          return concat(actions);
        })
      );
    })
  );

  @Effect({ dispatch: false })
  triggerPostPurchaseSurvey = this._actions.pipe(
    ofType(ActionsEnum.TRIGGER_POST_PURCHASE_SURVEY),
    switchMap(({ payload }: TriggerPostPurchaseSurvey) => {
      return this._settingsDataStore.getSettings().pipe(
        withLatestFrom(this._featuresDataStore.featureToggles$),
        filter(([settings, toggles]: [Settings, Toggles]) => {
          return (
            toggles.isAllAccess(Toggles.KEYS.POST_PURCHASE_SURVEY) &&
            !settings.postPurchaseInfo.isSurveyDone()
          );
        }),
        // set timeout so title and header is updated before open the dialog
        tap(() =>
          setTimeout(() =>
            this._dialogService.openDialog({ type: POST_PURCHASE_DIALOG_TYPE })
          )
        )
      );
    })
  );

  @Effect()
  dismissPostPurchaseSurvey: Observable<
    SaveSettingsNetwork | LoadPromoBanners | SetLocalStorageItem
  > = this._actions.pipe(
    ofType(ActionsEnum.DISMISS_POST_PURCHASE_SURVEY),
    concatMap(() => {
      return this._accountDataStore.getAccount().pipe(
        flatMap((account: Account) => {
          const effects = [];
          const key = this._homeDataBuilder.buildPostPurchaseSurveyKey(
            account.accountId
          );
          const counter = (+this._localStorageService.getItem(key) || 0) + 1;

          if (counter >= MAX_POST_PURCHASE_SURVEY_COUNTER) {
            const postPurchaseInfoProps = this._homeDataBuilder.buildSettingsPostPurchaseInfoProps(
              PostPurchaseStateEnum.dismissed
            );

            effects.push(
              updateSettings(postPurchaseInfoProps),
              saveCurrentSettingsNetwork(v4())
            );
          } else {
            effects.push(setLocalStorageItem(key, counter));
          }

          return concat(effects);
        })
      );
    })
  );

  @Effect()
  fetchTask: Observable<SetTask | FetchTaskError> = this._actions.pipe(
    ofType(ActionsEnum.FETCH_TASK),
    switchMap(() => {
      return this._homeDataService.fetchTask().pipe(
        map((task) => setTask(task)),
        catchError((error) =>
          of(fetchTaskError(new Error('fetch task error'), error))
        )
      );
    })
  );

  @Effect()
  fetchTaskV4: Observable<SetTask | FetchTaskError> = this._actions.pipe(
    ofType(ActionsEnum.FETCH_TASK_V4),
    switchMap(() => {
      return this._homeDataService.fetchTasksV4().pipe(
        map((task) => setTask(task)),
        catchError((error) =>
          of(fetchTaskError(new Error('fetch task error'), error))
        )
      );
    })
  );

  @Effect()
  setTaskItemState: Observable<
    SetTaskItemStateSuccess | SetTaskItemStateError
  > = this._actions.pipe(
    ofType(ActionsEnum.SET_TASK_ITEM_STATE),
    flatMap(({ payload }: SetTaskItemState) => {
      return this._homeDataService
        .setTaskItemState(payload.id, payload.state)
        .pipe(
          map(() => setTaskItemStateSuccess()),
          catchError((error) =>
            of(
              setTaskItemStateError(
                new Error('set task item state error'),
                error
              )
            )
          )
        );
    })
  );

  @Effect()
  setTaskCompletion: Observable<
    FetchTask | SetTaskCompletionError
  > = this._actions.pipe(
    ofType(ActionsEnum.SET_TASK_COMPLETION),
    switchMap(() => {
      return this._homeDataService.setTaskCompletion().pipe(
        map(() => fetchTask()),
        catchError((error) =>
          of(
            setTaskCompletionError(
              new Error('set task completion error'),
              error
            )
          )
        )
      );
    })
  );

  @Effect()
  handleInsightsHubDeeplink: Observable<TrackEvent> = this._actions.pipe(
    ofType(ActionsEnum.HANDLE_INSIGHTS_HUB_DEEPLINK),
    map(({ payload }: IHandleInsightsHubDeeplink) => {
      const { deeplink, reportType, trackingScreenName } = payload;

      const deeplinkProperties: IDeepLinkProperties = this._navigationBuilder.buildDeepLinkProperties(
        deeplink
      );
      const trackingAdditionalProperties: ITrackingProperties = {
        type: deeplinkProperties.path,
        ...deeplinkProperties.params,
        link: deeplink,
        ...this._trackingAdditionalPropertiesBuilder.buildInsightsHub(true),
      };

      this._navigationService.goToDeepLink(deeplink);

      return trackEvent(
        this._trackingObjectBuilder.buildReportExpanded(reportType),
        this._trackingActionBuilder.buildButtonTapped(
          'deeplink',
          trackingScreenName
        ),
        trackingAdditionalProperties
      );
    })
  );
}
